import type { UseMutationResult } from "@tanstack/react-query"
import { useMutation, useQueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import {
  invalidateSolarPriceDependents,
  invalidateSolarPricingInputSteps,
} from "./solar-price-dependents"
import type { SolarProposalFinancingDto } from "../dto/solar-proposal-financing.dto"
import { solarProposalService } from "../services/solar-proposal.service"

export type SelectSolarLoanProductVariables = {
  proposalId: string
  body: { loanProductId: string }
}

/**
 * Saves the Credit Human Loan Product. The answer is the financing read
 * itself. The term, APR and payment factor are pricing inputs, so pricing and
 * savings report out of date until refreshed on purpose; nothing recalculates.
 * A failure refetches the read, since a lock or a retired product may be why.
 */
export default function useSelectSolarLoanProduct(): UseMutationResult<
  SolarProposalFinancingDto,
  unknown,
  SelectSolarLoanProductVariables
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ proposalId, body }: SelectSolarLoanProductVariables) =>
      solarProposalService.putLoanProduct(proposalId, body),
    onSuccess: async (saved, { proposalId }) => {
      queryClient.setQueryData(
        queryKeys.solarProposal.financing(proposalId),
        saved,
      )
      // Pricing first, awaited: the saved notice reads its status.
      await queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.pricing(proposalId),
        exact: true,
      })
      invalidateSolarPriceDependents(queryClient, proposalId, {
        except: [`pricing`, `financing`],
      })
      // VPP estimates follow the loan term.
      invalidateSolarPricingInputSteps(queryClient, proposalId)
      void queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.summary(proposalId),
        exact: true,
      })
    },
    onError: (_error, { proposalId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.solarProposal.financing(proposalId),
        exact: true,
      })
    },
  })
}
